import * as React from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { useTranslation } from "react-i18next";

import { CircularProgress, Grid, Typography } from "@mui/material";
import axios from "axios";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import ColorBasedTheme from "../../common/theme/colorTheme/ColorBackgroundTheme";
import ImageBasedTheme from "../../common/theme/imageTheme/ImageBackgroundTheme";
import WinterTheme from "../../common/theme/winter/WinterTheme";
import "./lottery.css";
import LotteryDialog from "./LotteryDialog";
import RssFeeder from "./RssFeeder";
import LotteryUpdateMarquee from "./LotteryUpdateMarquee";

interface LotteryListRendererProps {
  loading: boolean;
  count?: number;
  data: any;
  // Set when the display was opened as /lottery?screen=N: the settings for that
  // screen, so two screen tabs do not share one localStorage copy between them.
  settingsOverride?: any;
  screenOneSettingsOverride?: any;
}

const LotteryListRenderer: React.FC<LotteryListRendererProps> = ({ loading, count, data, settingsOverride, screenOneSettingsOverride }) => {
  const { t } = useTranslation("translation");
  //@ts-ignore
  const config = window.config;
  const currencySymbol = config.currencySymbol ?? "$";
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";
  const rowsCount = config?.ticketRows ?? 3;
  const columnCount = config.ticketColumns ?? 6;
  const lotteryTicketHeight = config.lotteryTicketHeight ?? 65;
  const [open, setOpen] = React.useState(false);
  const [lotteryIndex, setLotteryIndex] = React.useState(0);
  const [lotteryData, setLotteryData] = React.useState({});

  let localData = localStorage.getItem("settings");
  const storedSettings = localData ? JSON.parse(localStorage.getItem("settings") || "") : null;
  const settings = settingsOverride ?? storedSettings;
  const subscriptionDetails = JSON.parse(localStorage.getItem("subscription") || "");

  let screenOneSettingsLocalData = localStorage.getItem("screenOneSettings");
  const storedScreenOneSettings = screenOneSettingsLocalData ? JSON.parse(localStorage.getItem("screenOneSettings") || "") : null;
  const screenOneSettings = screenOneSettingsOverride ?? storedScreenOneSettings;

  const uId = JSON.parse(localStorage.getItem("UserData") || "");
  const [adsHeaderData, setAdsHeaderData] = React.useState<Array<any>>([]);
  const [adsFooterData, setAdsFooterData] = React.useState<Array<any>>([]);
  const [isFullscreen, setIsFullscreen] = React.useState<boolean>(false);
  const { showErrorMessage } = useSnackbarAlert();
  const fullscreenHandler = useFullScreenHandle();

  React.useEffect(() => {
    const payload = {
      pageNumber: 1,
      pageSize: 6,
      adLocation: ["HEADER"],
      userId: uId ? uId?.uId : "",
    };

    if (settings?.headerFooterStyle == "ADVERTISEMENT_HEADER") {
      axios
        .post(`${apiBaseURL}/api/advertisement/getAdvertisementDetails`, payload)
        .then((res: any) => {
          setAdsHeaderData(res?.data?.advertisements);
        })
        .catch((error: Error) => showErrorMessage(error));
    }
  }, []);

  React.useEffect(() => {
    const payload = {
      pageNumber: 1,
      pageSize: 6,
      adLocation: ["FOOTER"],
      userId: uId ? uId?.uId : "",
    };
    if (settings?.headerFooterStyle == "ADVERTISEMENT_FOOTER") {
      axios
        .post(`${apiBaseURL}/api/advertisement/getAdvertisementDetails`, payload)
        .then((res: any) => {
          setAdsFooterData(res?.data?.advertisements);
        })
        .catch((error: Error) => showErrorMessage(error));
    }
  }, []);

  const fullscreenCallback = React.useCallback((state: any, handle: any) => {
    console.log("Inside fullscreen mode");
    console.log("State value - ", state);
    console.log("Handle value - ", handle);
    setIsFullscreen(state);
  }, []);

  const images: any = {
    COMING_SOON: "https://lottery-live.s3.us-east-2.amazonaws.com/Coming+Soon.jpg",
    SOLD_OUT: "https://lottery-live.s3.us-east-2.amazonaws.com/Sold+Out.jpg",
    EMPTY: "",
  };

  // Measures the real space left for the ticket grid (see the sizing effect below).
  const gridRef = React.useRef<HTMLDivElement | null>(null);

  const [boxDimensions, setBoxDimensions] = React.useState({
    rowsCount: 0,
    columnCount: 0,
    parentBoxHeight: 0,
    parentBoxWidth: 0,
    testHeight: 0,
    boxTestHeight: 0,
    boxTestWidth: 0,
  });

  // Sizing the ticket grid. Re-run whenever the space available changes: the header
  // loads its content asynchronously (RSS marquee / ad images), so its height is not
  // final at mount, and a display can also be resized. Guarded against re-render
  // loops by only committing dimensions that actually differ.
  const applyBoxDimensions = React.useCallback((next: any) => {
    setBoxDimensions((prev: any) => {
      const same = Object.keys(next).every((k) => Math.abs((prev?.[k] ?? 0) - next[k]) < 0.5);
      return same ? prev : next;
    });
  }, []);

  React.useEffect(() => {
    const computeBoxDimensions = () => {
      console.log("Window innerheight - ", window.innerHeight);
      if (settings?.orientation == "landscape") {
        // LANDSCAPE ORIENTATION
        let height = window.innerHeight;
        let percentage = 0;
        let boxHeight = 0;
        let boxWidth = 0;
        let minusHeight = 20;
        if (
          settings?.headerFooterStyle == "ADVERTISEMENT_HEADER" &&
          settings?.headerFooterStyle == "ADVERTISEMENT_FOOTER"
        ) {
          percentage = Math.floor((30 / 100) * window.innerHeight);
          height = height - percentage;
          minusHeight = 4;
        } else if (
          settings?.headerFooterStyle == "ADVERTISEMENT_HEADER" ||
          settings?.headerFooterStyle == "LOTTERY_UPDATE_HEADER"
        ) {
          percentage = Math.floor((20 / 100) * window.innerHeight);
          console.log("Percentage - ", percentage);
          height = height - percentage;
          minusHeight = 4;
        } else if (
          settings?.headerFooterStyle == "ADVERTISEMENT_FOOTER" ||
          settings?.headerFooterStyle == "LOTTERY_UPDATE_FOOTER"
        ) {
          percentage = Math.floor((20 / 100) * window.innerHeight);
          height = height - percentage;
          minusHeight = 4;
        }
        console.log("Height - ", height);

        // Each tile carries `margin: 0.5%` (lottery.css). A PERCENTAGE margin resolves
        // against the container's WIDTH, not its height, so every row really occupies
        // boxHeight + 1% of the container width. That gap was never subtracted here, so
        // with 8 rows the grid ran ~100px taller than the space reserved for it and the
        // last row was cut off by the `overflow: hidden` on .front-cards-user-side.
        const containerWidth = gridRef.current?.clientWidth || window.innerWidth;
        const rowGap = 0.01 * containerWidth;
        const containerPadding = 12; // .parent-box-container padding: 8px top + 4px bottom

        // The header reserve above is a flat percentage of the screen, which over-reserves
        // on a tall screen and under-reserves on a short one. When the header has actually
        // been laid out, use what it really occupies and give the rest to the tickets.
        const gridRect = gridRef.current?.getBoundingClientRect();
        // width > 0 means the container is actually laid out; top may legitimately be
        // 0 when there is no header at all
        if (gridRect && gridRect.width > 0 && gridRect.top >= 0 && gridRect.top < window.innerHeight) {
          height = window.innerHeight - gridRect.top;
        }

        if (settings?.rows) {
          // every tile has a margin on BOTH sides, so each row occupies
          // boxHeight + rowGap - including the first, whose top margin shifts the
          // whole grid down
          boxHeight = (height - containerPadding) / settings?.rows - rowGap;
        }

        if (settings?.column) {
          boxWidth = (1 * window.innerWidth) / settings?.column;
        }

        applyBoxDimensions({
          columnCount: settings?.column, //For width of individual box
          rowsCount: settings?.rows, //For height of individual box
          parentBoxHeight: lotteryTicketHeight,
          parentBoxWidth: 100,
          testHeight: height,
          boxTestHeight: boxHeight,
          boxTestWidth: boxWidth,
        });
      } else {
        // VERTICAL ORIENTATION
        let height = window.innerWidth;
        let width = window.innerHeight;
        let percentage = 0;
        let boxHeight = 0;
        let boxWidth = 0;
        let minusHeight = 20;

        // Calculate header space
        let hasHeader = false;
        let hasFooter = false;

        if (settings?.headerFooterStyle == "ADVERTISEMENT_HEADER") {
          percentage = Math.floor((12 / 100) * height);
          height = height - percentage;
          hasHeader = true;
          minusHeight = 10;
        } else if (settings?.headerFooterStyle == "LOTTERY_UPDATE_HEADER") {
          percentage = Math.floor((8 / 100) * height);
          console.log("Percentage - ", percentage);
          height = height - percentage;
          hasHeader = true;
          minusHeight = 10;
        }

        // Calculate footer space
        if (settings?.headerFooterStyle == "ADVERTISEMENT_FOOTER") {
          percentage = Math.floor((12 / 100) * height);
          height = height - percentage;
          hasFooter = true;
          minusHeight = 10;
        } else if (settings?.headerFooterStyle == "LOTTERY_UPDATE_FOOTER") {
          percentage = Math.floor((8 / 100) * height);
          height = height - percentage;
          hasFooter = true;
          minusHeight = 10;
        }

        // If no header or footer, use more space
        if (!hasHeader && !hasFooter) {
          minusHeight = 12;
        }
        console.log("Height - ", height);

        // Same 0.5% per-tile margin as the landscape branch. In the rotated frame the
        // rows run along the swapped axis, so the margin resolves against `width`.
        const rowGapVertical = 0.01 * width;
        const containerPaddingVertical = 8; // .parent-box-container left+right padding

        if (settings?.rows) {
          // `minusHeight` is deliberately NOT subtracted here any more: it was a fudge
          // standing in for the per-row spacing, and now that the real margin is taken
          // out it only left a dead band (~10px per row) along the edge of the screen.
          boxHeight =
            (height - containerPaddingVertical) / settings?.rows - rowGapVertical;
        }

        if (settings?.column) {
          boxWidth = (1 * width) / settings?.column;
        }

        applyBoxDimensions({
          columnCount: settings?.column, //For width of individual box
          rowsCount: settings?.rows, //For height of individual box
          parentBoxHeight: lotteryTicketHeight,
          parentBoxWidth: 100,
          testHeight: height,
          boxTestHeight: boxHeight,
          boxTestWidth: boxWidth,
        });
      }
    };

    computeBoxDimensions();

    // The header fills in asynchronously (RSS marquee, ad images) and that changes how
    // much room is left for the tickets. A ResizeObserver cannot detect it: a taller
    // header MOVES the grid down without changing the size of any element we could
    // observe. On a cold load the header was still empty when we measured, so the first
    // open fell back to the flat percentage reserve and drew shorter tiles than the
    // second (cached) open - the two looked different for the same screen.
    // Re-measuring over the first few seconds settles both to the same result; the
    // equality guard makes the repeat passes free.
    const settleDelays = [0, 100, 300, 600, 1000, 1500, 2500, 4000];
    const settleTimers = settleDelays.map((delay) => setTimeout(computeBoxDimensions, delay));
    window.addEventListener("resize", computeBoxDimensions);
    return () => {
      settleTimers.forEach(clearTimeout);
      window.removeEventListener("resize", computeBoxDimensions);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.rows, settings?.column, settings?.orientation, settings?.headerFooterStyle]);

  // if(loading) {
  //     return(
  //         <CircularProgress
  //             color='inherit'
  //             size={30}
  //             thickness={6}
  //             style={{ marginLeft: "8px" }}
  //         />
  //     );
  // }

  return (
    <>
      <FullScreen handle={fullscreenHandler} onChange={fullscreenCallback}>
        {settings?.orientation == "landscape" ? (
          // Start of landscape orientation

          <Grid className='position-relative'>
            <Grid
              className='front-cards-user-side'
              style={{
                overflow: "hidden",
              }}
            >
              {settings?.headerFooterStyle == "ADVERTISEMENT_HEADER" && (
                <Grid className='header' p={2}>
                  <Grid className='header-left'>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[0]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[0]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[0]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[1]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[1]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[1]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[2]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[2]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[2]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <div className='vertical_dotted_line'></div>
                  <Grid className='header-center'>
                    <Typography className='header-typography-main'>
                      {t("welcomeShopText")}
                    </Typography>
                    <Typography className='header-typography-description'>
                      {t("welcomeShopDescription")}
                    </Typography>
                  </Grid>
                  <div className='vertical_dotted_line'></div>
                  <Grid className='header-right'>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[3]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[3]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[3]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[4]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[4]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[4]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[5]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[5]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[5]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {settings?.headerFooterStyle == "LOTTERY_UPDATE_HEADER" && (
                <Grid className='header lottery-header-marquee' style={{ height: "12%", padding: "4px 10px", overflow: "visible" }}>
                  <LotteryUpdateMarquee
                    feedUrl='https://www.texaslottery.com/export/sites/lottery/rss/tlc_latest.xml'
                    interval={900000}
                    position='header'
                    backendEndpoint={`${apiBaseURL}/api/lottery/rss-feed`}
                  />
                </Grid>
              )}
              {loading ? (
                <CircularProgress
                  color='inherit'
                  size={30}
                  thickness={6}
                  style={{ color: "white", justifySelf: "center" }}
                />
              ) : (
                <Grid
                  className='w-full'
                  style={{
                    height: `${boxDimensions?.testHeight}px`,
                    maxHeight: `${boxDimensions?.testHeight}px`,
                  }}
                >
                  <Grid
                    ref={gridRef}
                    className='parent-box-container'
                    style={{
                      height: `${boxDimensions?.parentBoxHeight}%`,
                      width: `${boxDimensions?.boxTestWidth - 2}%`,
                    }}
                  >
                    {Array(parseInt(settings?.ticketsCount ?? 0))
                      .fill(parseInt(settings?.ticketsCount ?? 0))
                      ?.slice(0, parseInt(settings?.ticketsCount ?? 0))
                      ?.map((item: any, index: any) => {
                        return (
                          <Grid
                            className={`lottery-box position-relative ${
                              data[index]?.isAnimate === true && "zoom-in-out-box"
                            }`}
                            key={index}
                            onClick={() => {
                              setLotteryData(data[index]);
                              setLotteryIndex(index);
                              setOpen(true);
                            }}
                            style={{
                              width: `${
                                boxDimensions?.parentBoxWidth / boxDimensions?.columnCount - 1
                              }%`,
                              height: `${boxDimensions?.boxTestHeight}px`,
                              cursor: "pointer",
                            }}
                          >
                            <Grid
                              style={{
                                width: `100%`,
                                height: `100%`,
                                backgroundImage: !loading
                                  ? data[index]?.isSoldOut
                                    ? `url(${images["SOLD_OUT"]})`
                                    : data[index]?.imageUrl
                                      ? `url(${data[index]?.imageUrl})`
                                      : settings?.emptyTicketsDisplay in images
                                        ? `url(${images[settings?.emptyTicketsDisplay]})`
                                        : ""
                                  : "",
                                backgroundSize: `100% 100%`,
                                filter: data[index]?.isWinner ? "blur(8px)" : "none",
                              }}
                            ></Grid>
                            {data[index]?.isWinner && (
                              <div className='zoom-in-out-winner-box winner-box'>
                                <Typography className='winner-typography-dialog'>
                                  Lucky ticket,
                                </Typography>
                                <Typography className='winner-typography-dialog'>
                                  customer wins
                                </Typography>
                                {(data[index]?.prizeMoney !== 0 ||
                                  data[index]?.prizeMoney !== "0" ||
                                  data[index]?.prizeMoney !== null) &&
                                  data[index]?.prizeMoney !== "" && (
                                    <Typography className='winner-typography-dialog'>
                                      {currencySymbol + data[index]?.prizeMoney}
                                    </Typography>
                                  )}
                              </div>
                            )}
                            <div className='container'>
                              <div className='circle'>
                                <b>
                                  <span className='circleText extraBold'>{screenOneSettings?.ticketsCount ? (screenOneSettings?.ticketsCount + index + 1) : (index + 1)}</span>
                                </b>
                              </div>
                            </div>
                            {data[index]?.ticketPrice && (
                              <div className='ribbon ribbon-top-left extraBold'>
                                <span
                                  style={{
                                    border: data[index]?.ticketPrice && "0.07rem solid black",
                                    fontSize: "1.5rem",
                                    fontWeight: "bolder",
                                  }}
                                >
                                  {data[index]?.ticketPrice
                                    ? currencySymbol + data[index]?.ticketPrice
                                    : null}
                                </span>
                              </div>
                            )}
                            {data[index]?.isNew === true && (
                              <div className='newLabel newLabel-top-right'>
                                <img src='/new.gif' alt='new' />
                              </div>
                            )}
                          </Grid>
                        );
                      })}
                  </Grid>
                </Grid>
              )}
              {settings?.headerFooterStyle == "LOTTERY_UPDATE_FOOTER" && (
                <Grid className='footer lottery-header-marquee' style={{ height: "12%", padding: "4px 10px", overflow: "visible" }}>
                  <LotteryUpdateMarquee
                    feedUrl='https://www.texaslottery.com/export/sites/lottery/rss/tlc_latest.xml'
                    interval={900000}
                    position='footer'
                    backendEndpoint={`${apiBaseURL}/api/lottery/rss-feed`}
                  />
                </Grid>
              )}
              {settings?.headerFooterStyle == "ADVERTISEMENT_FOOTER" && (
                <Grid className='footer'>
                  <Grid className='header-left'>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[0]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[0]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[0]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[1]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[1]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[1]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[2]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[2]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[2]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <div className='vertical_dotted_line'></div>
                  <Grid className='header-center'>
                    <Typography className='header-typography-main'>
                      {t("welcomeShopText")}
                    </Typography>
                    <Typography className='header-typography-description'>
                      {t("welcomeShopDescription")}
                    </Typography>
                  </Grid>
                  <div className='vertical_dotted_line'></div>
                  <Grid className='header-right'>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[3]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[3]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[3]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[4]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[4]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[4]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[5]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[5]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[5]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
            {settings?.backgroundType === "THEME" && settings?.backgroundValue === "SNOWFLAKES" && (
              <WinterTheme />
            )}

            {settings?.backgroundType === "COLOR" && settings?.backgroundValue !== "" && (
              <ColorBasedTheme
                colors={
                    settings?.backgroundValue ?? "#FF0000,#FCFF00,#0EECFF"
                }
                verticalOrientation={false}
              />
            )}

            {settings?.backgroundType === "IMAGE" && settings?.backgroundValue !== "" && (
              <ImageBasedTheme image={settings?.backgroundValue} verticalOrientation={false} />
            )}

            <LotteryDialog
              data={lotteryData}
              open={open}
              setOpen={setOpen}
              lotteryIndex={lotteryIndex}
              images={images}
              settings={settings}
            />
          </Grid>
        ) : (
          // End of landscape orientation

          // Start of vertical orientation

          <Grid className='vertical-parent'>
            <Grid
              className='front-cards-user-side'
              style={{
                height: "100vw",
                width: "100vh",
                overflow: "hidden",
              }}
            >
              {settings?.headerFooterStyle == "ADVERTISEMENT_HEADER" && (
                <Grid className='header' p={2}>
                  <Grid className='header-left'>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[0]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[0]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[0]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[1]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[1]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[1]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[2]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[2]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[2]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <div className='vertical_dotted_line'></div>
                  <Grid className='header-center'>
                    <Typography className='header-typography-main'>
                      {t("welcomeShopText")}
                    </Typography>
                    <Typography className='header-typography-description'>
                      {t("welcomeShopDescription")}
                    </Typography>
                  </Grid>
                  <div className='vertical_dotted_line'></div>
                  <Grid className='header-right'>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[3]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[3]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[3]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[4]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[4]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[4]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsHeaderData?.[5]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsHeaderData?.[5]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsHeaderData?.[5]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {settings?.headerFooterStyle == "LOTTERY_UPDATE_HEADER" && (
                <Grid className='header lottery-header-marquee lottery-header-marquee-vertical' style={{ padding: "4px 10px", overflow: "visible" }}>
                  <LotteryUpdateMarquee
                    feedUrl='https://www.texaslottery.com/export/sites/lottery/rss/tlc_latest.xml'
                    interval={900000}
                    position='header'
                    backendEndpoint={`${apiBaseURL}/api/lottery/rss-feed`}
                  />
                </Grid>
              )}
              {loading ? (
                <CircularProgress
                  color='inherit'
                  size={30}
                  thickness={6}
                  style={{ color: "white", justifySelf: "center" }}
                />
              ) : (
                <Grid
                  className='w-full'
                  style={{
                    width: `${boxDimensions?.testHeight}px`,
                    maxWidth: `${boxDimensions?.testHeight}px`,
                    // height: "100vw",
                    // width: "100vh",
                  }}
                >
                  <Grid
                    className='parent-box-container'
                    style={{
                      width: `${boxDimensions?.boxTestWidth - 10}%`,
                      height: `${boxDimensions?.parentBoxHeight}%`,
                      // height: "100vw",
                      // width: "100vh",
                    }}
                  >
                    {Array(parseInt(settings?.ticketsCount ?? 0))
                      .fill(parseInt(settings?.ticketsCount ?? 0))
                      ?.slice(0, parseInt(settings?.ticketsCount ?? 0))
                      ?.map((item: any, index: any) => {
                        return (
                          <Grid
                            className={`lottery-box position-relative ${
                              data[index]?.isAnimate === true && "zoom-in-out-box"
                            }`}
                            key={index}
                            onClick={() => {
                              setLotteryData(data[index]);
                              setLotteryIndex(index);
                              setOpen(true);
                            }}
                            style={{
                              width: `${
                                boxDimensions?.parentBoxWidth / boxDimensions?.columnCount - 1
                              }%`,
                              height: `${boxDimensions?.boxTestHeight}px`,
                              cursor: "pointer",
                            }}
                          >
                            <Grid
                              style={{
                                width: `100%`,
                                height: `100%`,
                                backgroundImage: !loading
                                  ? data[index]?.isSoldOut
                                    ? `url(${images["SOLD_OUT"]})`
                                    : data[index]?.imageUrl
                                      ? `url(${data[index]?.imageUrl})`
                                      : settings?.emptyTicketsDisplay in images
                                        ? `url(${images[settings?.emptyTicketsDisplay]})`
                                        : ""
                                  : "",
                                backgroundSize: `100% 100%`,
                                filter: data[index]?.isWinner ? "blur(8px)" : "none",
                              }}
                            ></Grid>
                            {data[index]?.isWinner && (
                              <div className='zoom-in-out-winner-box winner-box'>
                                <Typography className='winner-typography-dialog'>
                                  Lucky ticket,
                                </Typography>
                                <Typography className='winner-typography-dialog'>
                                  customer wins
                                </Typography>
                                <Typography className='winner-typography-dialog'>
                                  {currencySymbol + data[index]?.prizeMoney}
                                </Typography>
                              </div>
                            )}
                            <div className='container'>
                              <div className='circle'>
                                <b>
                                  <span className='circleText extraBold'>{screenOneSettings?.ticketsCount ? (screenOneSettings?.ticketsCount + index + 1) : (index + 1)}</span>
                                </b>
                              </div>
                            </div>
                            {data[index]?.ticketPrice && (
                              <div className='ribbon ribbon-top-left extraBold'>
                                <span
                                  style={{
                                    border: data[index]?.ticketPrice && "0.07rem solid black",
                                    fontSize: "1.5rem",
                                    fontWeight: "bolder",
                                  }}
                                >
                                  {data[index]?.ticketPrice
                                    ? currencySymbol + data[index]?.ticketPrice
                                    : null}
                                </span>
                              </div>
                            )}
                            {data[index]?.isNew === true && (
                              <div className='newLabel newLabel-top-right'>
                                <img src='/new.gif' alt='new' />
                              </div>
                            )}
                          </Grid>
                        );
                      })}
                  </Grid>
                </Grid>
              )}
              {settings?.headerFooterStyle == "LOTTERY_UPDATE_FOOTER" && (
                <Grid className='footer lottery-header-marquee' style={{ height: "12%", padding: "4px 10px", overflow: "visible" }}>
                  <LotteryUpdateMarquee
                    feedUrl='https://www.texaslottery.com/export/sites/lottery/rss/tlc_latest.xml'
                    interval={900000}
                    position='footer'
                    backendEndpoint={`${apiBaseURL}/api/lottery/rss-feed`}
                  />
                </Grid>
              )}
              {settings?.headerFooterStyle == "ADVERTISEMENT_FOOTER" && (
                <Grid className='footer'>
                  <Grid className='header-left'>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[0]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[0]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[0]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[1]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[1]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[1]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[2]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[2]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[2]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <div className='vertical_dotted_line'></div>
                  <Grid className='header-center'>
                    <Typography className='header-typography-main'>
                      {t("welcomeShopText")}
                    </Typography>
                    <Typography className='header-typography-description'>
                      {t("welcomeShopDescription")}
                    </Typography>
                  </Grid>
                  <div className='vertical_dotted_line'></div>
                  <Grid className='header-right'>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[3]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[3]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[3]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[4]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[4]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[4]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={3} className='ad-header'>
                        <img
                          src={adsFooterData?.[5]?.imageUrl}
                          height='100%'
                          width='80%'
                          className='p-6'
                          alt=''
                        />
                        <Grid>
                          <Typography className='field-title'>
                            {adsFooterData?.[5]?.productTitle}
                          </Typography>
                          <Typography className='field-value'>
                            {adsFooterData?.[5]?.productPriceDescription}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
            {settings?.backgroundType === "THEME" && settings?.backgroundValue === "SNOWFLAKES" && (
              <WinterTheme />
            )}

            {settings?.backgroundType === "COLOR" && settings?.backgroundValue !== "" && (
              <ColorBasedTheme
                colors={
                    settings?.backgroundValue ?? "#FF0000,#FCFF00,#0EECFF"
                }
                verticalOrientation={true}
              />
            )}

            {settings?.backgroundType === "IMAGE" && settings?.backgroundValue !== "" && (
              <ImageBasedTheme image={settings?.backgroundValue} verticalOrientation={true} />
            )}

            <LotteryDialog
              data={lotteryData}
              open={open}
              setOpen={setOpen}
              lotteryIndex={lotteryIndex}
              images={images}
              settings={settings}
              verticalOrientation={settings?.orientation != "landscape"}
            />
          </Grid>

          // End of vertical orientation
        )}
      </FullScreen>
    </>
  );
};
export default LotteryListRenderer;
